import { ApiError, UnauthorizedError } from '../api/client';

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="center">
      <div className="spinner" />
      {label ? <p className="muted">{label}</p> : null}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return <div className="error-banner">{message}</div>;
}

// The status badge collapses every persisted status (including legacy ones from
// old jobs) to one of the current pipeline steps, and gives each step its OWN
// colour so the list reads at a glance. Order/colours match the pipeline flow.
const CANON_STATUS: Record<string, string> = {
  DRAFT: 'DRAFT',
  QUOTE_SENT: 'QUOTE_SENT',
  PROPOSAL_SIGNED: 'PROPOSAL_SIGNED',
  AWAITING_DOCS: 'AWAITING_DOCS',
  REVIEW: 'REVIEW',
  QUERIES_SENT: 'QUERIES_SENT',
  QUERIES_ANSWERED: 'QUERIES_ANSWERED',
  REPORTING: 'REPORTING',
  DELIVERED: 'DELIVERED',
  // legacy -> nearest current step
  QUOTE_DRAFTED: 'DRAFT',
  DOCS_RECEIVED: 'AWAITING_DOCS',
  QUERIES_DRAFTED: 'REVIEW',
  CLOSED: 'QUERIES_SENT',
};

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  DRAFT: { label: 'Draft', bg: '#eef1f4', fg: '#5b6675' }, // grey
  QUOTE_SENT: { label: 'Quote sent', bg: '#eaf1fb', fg: '#2f5fb0' }, // blue
  PROPOSAL_SIGNED: { label: 'Proposal signed', bg: '#e1f4ef', fg: '#1f8a76' }, // teal
  AWAITING_DOCS: { label: 'Waiting for documents', bg: '#fdf1e3', fg: '#b0640f' }, // amber
  REVIEW: { label: 'Review', bg: '#f0edfc', fg: '#6a5acb' }, // purple
  QUERIES_SENT: { label: 'Queries sent', bg: '#e8f7ee', fg: '#2f8f52' }, // green
  QUERIES_ANSWERED: { label: 'Queries answered', bg: '#e4f0ff', fg: '#1f5fa8' }, // blue: client has come back
  REPORTING: { label: 'Reporting', bg: '#e7eafc', fg: '#3b3fb6' }, // indigo (report generating)
  DELIVERED: { label: 'Delivered', bg: '#e6eaf2', fg: '#1f3a5f' }, // navy (engagement complete)
};

export function StatusBadge({ status }: { status: string }) {
  const canon = CANON_STATUS[status] ?? 'DRAFT';
  const { label, bg, fg } = STATUS_META[canon];
  return (
    <span className="badge" style={{ background: bg, color: fg }}>
      {label}
    </span>
  );
}

// Turns any thrown value into a user-facing sentence (Cyber Node voice: plain,
// no em-dashes).
export function toMessage(err: unknown): string {
  if (err instanceof UnauthorizedError) return err.message;
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}
